import { Injectable } from '@angular/core';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import {
  BigQueryFieldSchema,
  BigQueryHttpService,
  BigQueryRepeatedValue,
  BigQueryResponse,
  BigQueryRow,
  BigQueryRowValue,
} from '@examdojo/auth';
import { getVariableColor, VariableColor } from '@examdojo/styles';
import { ColDef, ColGroupDef, GridApi, GridOptions, IRowNode, ValueFormatterParams } from 'ag-grid-community';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';

type iso_date = string; // e.g. "2025-09-15" (week-start ISO date)
type weekly_series = Record<iso_date, number>; // all series aggregated weekly

export interface GTM {
  syllabus_id: string;
  syllabus_name: string;
  syllabus_short_name: string;
  syllabus_program: string;
  syllabus_subject: string;
  course_levels: string[];
  exam_quarters: ExamQuarterEntry[];
}

export interface ExamQuarterEntry {
  exam_quarter: string;
  is_past_exam: boolean;
  total_size: number;
  weekly_cummulative_subscribers: weekly_series;
  weekly_active_users: weekly_series;
  monthly_active_users: weekly_series;
  activated_users: number;
  ai_costs: {
    total_to_date_in_eur: number;
    monthly_avg_in_eur: number;
    free_subscribers: {
      monthly_avg_grading_in_eur: number;
      monthly_avg_chat_in_eur: number;
    };
    paid_subscribers: {
      monthly_avg_grading_in_eur: number;
      monthly_avg_chat_in_eur: number;
    };
    grading_coin_median_usage: weekly_series;
  };
}

// Flattened interface for AG Grid display - each row is a syllabus-quarter combination
export interface UserAnalyticsData {
  syllabus_program: string;
  syllabus_short_name: string;
  exam_quarter: string;
  is_past_exam: boolean;

  // Basic metrics
  subscribers: number;
  total_size: number;
  penetration_rate: number;
  activated: number;
  activation_rate: number;
  wau: number;
  mau: number;

  // Sparkline data
  subscribers_sparkline?: Array<{ date: string; value: number }>;
  wau_sparkline?: Array<{ date: string; value: number }>;
  mau_sparkline?: Array<{ date: string; value: number }>;
  grading_coin_usage_sparkline?: Array<{ date: string; value: number }>;

  // AI Costs
  ai_total_costs_eur: number;
  ai_monthly_avg_eur: number;
  grading_free_eur: number;
  chat_free_eur: number;
  grading_paid_eur: number;
  chat_paid_eur: number;
}

@Injectable()
export class UserAnalyticsAgGridService {
  constructor(private readonly bigQueryService: BigQueryHttpService) {}

  readonly gridOptions: GridOptions<UserAnalyticsData> = {
    suppressCellFocus: true,
    suppressContextMenu: true,
    groupDisplayType: 'singleColumn',
    groupDefaultExpanded: -1,
    groupMaintainOrder: false, // Allow sorting to reorder groups based on aggregated values
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No data found',
      body: 'Please check your filters',
    },
    // Header configuration
    headerHeight: 40,
    groupHeaderHeight: 40,
    defaultColDef: {
      suppressHeaderMenuButton: true,
      width: 120,
      minWidth: 100,
      suppressMovable: true,
      menuTabs: [],
      resizable: true,
      sortable: true,
      floatingFilter: false,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: false,
      cellStyle: { textAlign: 'left' },
      headerClass: 'text-left',
    },
    autoGroupColumnDef: {
      headerName: 'Program',
      width: 200,
      minWidth: 180,
      maxWidth: 240,
      pinned: 'left',
      sortable: true,
      cellStyle: { textAlign: 'left' },
      headerClass: 'text-left',
    },
    suppressAggFuncInHeader: true,
    includeHiddenColumnsInQuickFilter: true,
    quickFilterText: '',
    groupAggFiltering: true,
    alwaysAggregateAtRootLevel: true,
    multiSortKey: 'ctrl',
    allowContextMenuWithControlKey: true,
    columnHoverHighlight: true,
    getRowStyle: (params) => {
      // Apply styles to group rows (program rows like "IBDP", "HSC")
      if (params.node.group) {
        const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

        if (isDarkMode) {
          return {
            fontWeight: '600', // Semi-bold instead of bold
            backgroundColor: 'rgb(31 41 55)', // Dark gray background for dark mode
            borderBottom: '1px solid rgb(55 65 81)', // Darker border for dark mode
            color: 'rgb(229 231 235)', // Light text for dark mode
          };
        } else {
          return {
            fontWeight: '600', // Semi-bold instead of bold
            backgroundColor: 'rgb(249 250 251)', // Light gray background for light mode
            borderBottom: '1px solid rgb(229 231 235)', // Gray border for light mode
            color: 'rgb(31 41 55)', // Dark text for light mode
          };
        }
      }
      return undefined;
    },
    pinnedBottomRowData: [], // Will be populated dynamically
    onFilterChanged: (event) => {
      // Recalculate totals row when filters change
      this.updateTotalsRowFromFilteredData(event.api);

      // Force re-aggregation when filters change (both applied and removed)
      setTimeout(() => {
        // Refresh the client-side model to trigger custom aggregation recalculation
        event.api.refreshClientSideRowModel('aggregate');

        // Find and refresh group rows specifically to update aggregated values
        const groupRows: Array<IRowNode<UserAnalyticsData>> = [];
        event.api.forEachNode((node) => {
          if (node.group) {
            groupRows.push(node);
          }
        });

        if (groupRows.length > 0) {
          // Force refresh of group row cells to show updated aggregations
          event.api.refreshCells({
            rowNodes: groupRows,
            force: true,
          });
        }
      }, 0);
    },
  };

  generateColumnDefs(): Array<ColDef<UserAnalyticsData> | ColGroupDef<UserAnalyticsData>> {
    const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
    const subscribersColor = getVariableColor(VariableColor.Primary300, isDarkMode);
    const wauColor = getVariableColor(VariableColor.AssetHealthHealthy, isDarkMode);
    const gradingCoinColor = getVariableColor(VariableColor.Primary300, isDarkMode);

    return [
      // Group by program (first level)
      {
        field: 'syllabus_program',
        hide: true,
        rowGroup: true,
        cellClass: 'font-medium text-gray-900 dark:text-gray-100',
      },
      // Group by syllabus short name (second level)
      {
        field: 'syllabus_short_name',
        hide: true,
        rowGroup: true,
        cellClass: 'font-medium text-gray-900 dark:text-gray-100',
      },
      {
        field: 'exam_quarter',
        headerName: 'Exam Quarter',
        filter: 'agSetColumnFilter',
        width: 100,
        minWidth: 80,
        filterParams: {
          filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
          defaultOption: 'contains',
        },
      },
      // Column for filtering past exams (visible for debugging)
      {
        field: 'is_past_exam',
        headerName: 'Past Exam',
        width: 100,
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
        },
      },
      // Metrics columns
      {
        field: 'subscribers',
        headerName: 'Subscribers',
        width: 120,
        minWidth: 100,
        type: 'numericColumn',
        aggFunc: (params) => {
          // Custom aggregation: only sum visible/displayed leaf nodes
          const leafChildren = params.rowNode.allLeafChildren || [];
          const visibleChildren = leafChildren.filter((child) => child.displayed && !child.group);
          return visibleChildren.reduce((sum, node) => {
            const value = node.data?.subscribers;
            return sum + (typeof value === 'number' ? value : 0);
          }, 0);
        },
        cellStyle: { textAlign: 'right' },
        headerClass: 'text-right',
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) {
            return '';
          }
          return params.value.toLocaleString();
        },
      },
      {
        field: 'activated',
        headerName: 'Activated',
        width: 130,
        minWidth: 100,
        type: 'numericColumn',
        aggFunc: (params) => {
          // Custom aggregation: only sum visible/displayed leaf nodes
          const leafChildren = params.rowNode.allLeafChildren || [];
          const visibleChildren = leafChildren.filter((child) => child.displayed && !child.group);
          return visibleChildren.reduce((sum, node) => {
            const value = node.data?.activated;
            return sum + (typeof value === 'number' ? value : 0);
          }, 0);
        },
        cellStyle: { textAlign: 'right' },
        headerClass: 'text-right',
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) {
            return '';
          }
          return params.value.toLocaleString();
        },
      },
      {
        field: 'activation_rate',
        headerName: 'Activation Rate',
        width: 140,
        minWidth: 120,
        aggFunc: (params) => {
          // Only consider visible (filtered) leaf children
          const rowDataItems =
            params.rowNode.allLeafChildren
              ?.filter((child) => !child.group && child.displayed)
              .map((child) => child.data)
              .filter(Boolean) ?? [];

          const hasValidData = rowDataItems.some((row) => {
            const activatedValue = row?.activated;
            const subscribersValue = row?.subscribers;
            return typeof activatedValue === 'number' && typeof subscribersValue === 'number';
          });

          if (!hasValidData) {
            return null;
          }

          const totalActivated = rowDataItems.reduce((sum, row) => {
            const activatedValue = row?.activated;
            return sum + (typeof activatedValue === 'number' ? activatedValue : 0);
          }, 0);
          const totalSubscribers = rowDataItems.reduce((sum, row) => {
            const subscribersValue = row?.subscribers;
            return sum + (typeof subscribersValue === 'number' ? subscribersValue : 0);
          }, 0);
          return totalSubscribers > 0 ? totalActivated / totalSubscribers : 0;
        },
        cellRenderer: (params: { value: number | null }) => {
          if (params.value === null || params.value === undefined) {
            return '';
          }
          return this.getProgressBarHTML(params.value, {
            thresholds: { redBelow: 20, yellowBelow: 30 },
          });
        },
      },
      {
        field: 'subscribers_sparkline',
        headerName: 'Δ Subscribers',
        width: 120,
        minWidth: 100,
        sortable: false,
        cellRendererSelector: (p) => {
          const data = p.value as Array<{ date: string; value: number }> | undefined;
          if (Array.isArray(data) && data.length > 0) {
            return {
              component: 'agSparklineCellRenderer',
              params: {
                sparklineOptions: {
                  type: 'area',
                  xKey: 'date',
                  yKey: 'value',
                  area: {
                    fill: subscribersColor,
                    stroke: subscribersColor,
                  },
                  highlightStyle: {
                    fill: subscribersColor,
                  },
                  tooltip: {
                    enabled: true,
                    renderer: (tp: { xValue: string; yValue: number }) => {
                      const dateStr = tp.xValue.toString();
                      const [y, m, d] = dateStr.split('-');
                      const ddmmyyyy = d && m && y ? `${d}-${m}-${y}` : dateStr;
                      return { title: tp.yValue.toLocaleString(), content: ddmmyyyy };
                    },
                  },
                },
              },
            };
          }
          return undefined;
        },
      },
      {
        field: 'total_size',
        headerName: 'Total Size',
        width: 120,
        minWidth: 100,
        type: 'numericColumn',
        aggFunc: (params) => {
          // Custom aggregation: only sum visible/displayed leaf nodes
          const leafChildren = params.rowNode.allLeafChildren || [];
          const visibleChildren = leafChildren.filter((child) => child.displayed && !child.group);
          return visibleChildren.reduce((sum, node) => {
            const value = node.data?.total_size;
            return sum + (typeof value === 'number' ? value : 0);
          }, 0);
        },
        cellStyle: { textAlign: 'right' },
        headerClass: 'text-right',
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) {
            return '';
          }
          return params.value.toLocaleString();
        },
      },
      {
        field: 'penetration_rate',
        headerName: 'Penetration Rate',
        width: 140,
        minWidth: 120,
        aggFunc: (params) => {
          // Only consider visible (filtered) leaf children
          const rowDataItems =
            params.rowNode.allLeafChildren
              ?.filter((child) => !child.group && child.displayed)
              .map((child) => child.data)
              .filter(Boolean) ?? [];

          const hasValidData = rowDataItems.some((row) => {
            const subscribersValue = row?.subscribers;
            const sizeValue = row?.total_size;
            return typeof subscribersValue === 'number' && typeof sizeValue === 'number';
          });

          if (!hasValidData) {
            return null;
          }

          const totalSubscribers = rowDataItems.reduce((sum, row) => {
            const subscribersValue = row?.subscribers;
            return sum + (typeof subscribersValue === 'number' ? subscribersValue : 0);
          }, 0);
          const totalSize = rowDataItems.reduce((sum, row) => {
            const sizeValue = row?.total_size;
            return sum + (typeof sizeValue === 'number' ? sizeValue : 0);
          }, 0);
          return totalSize > 0 ? totalSubscribers / totalSize : 0;
        },
        cellRenderer: (params: { value: number | null }) => {
          if (params.value === null || params.value === undefined) {
            return '';
          }
          return this.getProgressBarHTML(params.value, {
            thresholds: { redBelow: 20, yellowBelow: 50 },
          });
        },
      },
      {
        field: 'mau',
        headerName: 'MAU',
        width: 120,
        minWidth: 100,
        sortable: true,
        aggFunc: (params) => {
          // Custom aggregation: only sum visible/displayed leaf nodes
          const leafChildren = params.rowNode.allLeafChildren || [];
          const visibleChildren = leafChildren.filter((child) => child.displayed && !child.group);
          return visibleChildren.reduce((sum, node) => {
            const value = node.data?.mau;
            return sum + (typeof value === 'number' ? value : 0);
          }, 0);
        },
        cellStyle: { textAlign: 'right' },
        headerClass: 'text-right',
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) {
            return '';
          }
          return params.value.toLocaleString();
        },
      },
      {
        field: 'mau_sparkline',
        headerName: 'Δ MAU',
        width: 120,
        minWidth: 100,
        sortable: false,
        cellRendererSelector: (p) => {
          const data = p.value as Array<{ date: string; value: number }> | undefined;
          if (Array.isArray(data) && data.length > 0) {
            return {
              component: 'agSparklineCellRenderer',
              params: {
                sparklineOptions: {
                  type: 'area',
                  xKey: 'date',
                  yKey: 'value',
                  area: {
                    fill: wauColor, // Reuse WAU color for consistency
                    stroke: wauColor,
                  },
                  highlightStyle: {
                    fill: wauColor,
                  },
                  tooltip: {
                    enabled: true,
                    renderer: (tp: { xValue: string; yValue: number }) => {
                      const dateStr = tp.xValue.toString();
                      const [y, m, d] = dateStr.split('-');
                      const ddmmyyyy = d && m && y ? `${d}-${m}-${y}` : dateStr;
                      return { title: tp.yValue.toLocaleString(), content: ddmmyyyy };
                    },
                  },
                },
              },
            };
          }
          return undefined;
        },
      },
      {
        field: 'wau',
        headerName: 'WAU',
        width: 100,
        minWidth: 80,
        type: 'numericColumn',
        aggFunc: (params) => {
          // Custom aggregation: only sum visible/displayed leaf nodes
          const leafChildren = params.rowNode.allLeafChildren || [];
          const visibleChildren = leafChildren.filter((child) => child.displayed && !child.group);
          return visibleChildren.reduce((sum, node) => {
            const value = node.data?.wau;
            return sum + (typeof value === 'number' ? value : 0);
          }, 0);
        },
        cellStyle: { textAlign: 'right' },
        headerClass: 'text-right',
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === null || params.value === undefined) {
            return '';
          }
          return params.value.toLocaleString();
        },
      },
      {
        field: 'wau_sparkline',
        headerName: 'Δ WAU',
        width: 120,
        minWidth: 100,
        sortable: false,
        cellRendererSelector: (p) => {
          const data = p.value as Array<{ date: string; value: number }> | undefined;
          if (Array.isArray(data) && data.length > 0) {
            return {
              component: 'agSparklineCellRenderer',
              params: {
                sparklineOptions: {
                  type: 'area',
                  xKey: 'date',
                  yKey: 'value',
                  area: {
                    fill: wauColor,
                    stroke: wauColor,
                  },
                  highlightStyle: {
                    fill: wauColor,
                  },
                  tooltip: {
                    enabled: true,
                    renderer: (tp: { xValue: string; yValue: number }) => {
                      const dateStr = tp.xValue.toString();
                      const [y, m, d] = dateStr.split('-');
                      const ddmmyyyy = d && m && y ? `${d}-${m}-${y}` : dateStr;
                      return { title: tp.yValue.toLocaleString(), content: ddmmyyyy };
                    },
                  },
                },
              },
            };
          }
          return undefined;
        },
      },
      // AI Costs group - expandable/collapsible
      {
        headerName: 'AI Costs',
        openByDefault: false, // Start collapsed
        children: [
          {
            field: 'ai_total_costs_eur',
            headerName: 'Total Costs',
            width: 120,
            minWidth: 100,
            type: 'numericColumn',
            aggFunc: (params) => {
              // Custom aggregation: only sum visible/displayed leaf nodes
              const leafChildren = params.rowNode.allLeafChildren || [];
              const visibleChildren = leafChildren.filter((child) => child.displayed && !child.group);
              return visibleChildren.reduce((sum, node) => {
                const value = node.data?.ai_total_costs_eur;
                return sum + (typeof value === 'number' ? value : 0);
              }, 0);
            },
            cellStyle: { textAlign: 'right' },
            headerClass: 'text-right',
            // Always visible (both collapsed and expanded)
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined || typeof params.value !== 'number') {
                return '';
              }
              return `${params.value.toFixed(2)} EUR`;
            },
          },
          {
            field: 'ai_monthly_avg_eur',
            headerName: 'Monthly Avg',
            width: 120,
            minWidth: 100,
            type: 'numericColumn',
            aggFunc: (params) => {
              // Custom aggregation: only sum visible/displayed leaf nodes
              const leafChildren = params.rowNode.allLeafChildren || [];
              const visibleChildren = leafChildren.filter((child) => child.displayed && !child.group);
              return visibleChildren.reduce((sum, node) => {
                const value = node.data?.ai_monthly_avg_eur;
                return sum + (typeof value === 'number' ? value : 0);
              }, 0);
            },
            cellStyle: { textAlign: 'right' },
            headerClass: 'text-right',
            columnGroupShow: 'open', // Only show when expanded
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined || typeof params.value !== 'number') {
                return '';
              }
              return `${params.value.toFixed(2)} EUR`;
            },
          },
          {
            field: 'grading_coin_usage_sparkline',
            headerName: 'Grading Usage',
            width: 120,
            minWidth: 100,
            sortable: false,
            columnGroupShow: 'open', // Only show when expanded
            cellRendererSelector: (p) => {
              const data = p.value as Array<{ date: string; value: number }> | undefined;
              if (Array.isArray(data) && data.length > 0) {
                return {
                  component: 'agSparklineCellRenderer',
                  params: {
                    sparklineOptions: {
                      type: 'area',
                      xKey: 'date',
                      yKey: 'value',
                      area: {
                        fill: gradingCoinColor,
                        stroke: gradingCoinColor,
                      },
                      highlightStyle: {
                        fill: gradingCoinColor,
                      },
                      tooltip: {
                        enabled: true,
                        renderer: (tp: { xValue: string; yValue: number }) => {
                          const dateStr = tp.xValue.toString();
                          const [y, m, d] = dateStr.split('-');
                          const ddmmyyyy = d && m && y ? `${d}-${m}-${y}` : dateStr;
                          return { title: tp.yValue.toLocaleString(), content: ddmmyyyy };
                        },
                      },
                    },
                  },
                };
              }
              return undefined;
            },
          },
        ],
      },
    ];
  }

  getStaticColumnDefs(): Array<ColDef<UserAnalyticsData> | ColGroupDef<UserAnalyticsData>> {
    return this.generateColumnDefs();
  }

  // Keep this getter for backward compatibility
  get columnDefs(): Array<ColDef<UserAnalyticsData> | ColGroupDef<UserAnalyticsData>> {
    return this.generateColumnDefs();
  }

  getData(): Observable<{
    data: UserAnalyticsData[];
    columnDefs: Array<ColDef<UserAnalyticsData> | ColGroupDef<UserAnalyticsData>>;
    pinnedBottomRowData: UserAnalyticsData[];
    isLoading: boolean;
  }> {
    return this.fetchGtmData().pipe(
      map((gtmData: GTM[]) => {
        const transformedData = this.transformApiDataToGridFormat(gtmData);
        const dynamicColumnDefs = this.generateColumnDefs();
        const pinnedBottomRowData = this.calculateTotalsRow(transformedData);

        // Update grid options with pinned bottom row data
        this.gridOptions.pinnedBottomRowData = pinnedBottomRowData;

        return {
          data: transformedData,
          columnDefs: dynamicColumnDefs,
          pinnedBottomRowData,
          isLoading: false,
        };
      }),
      startWith({ data: [], columnDefs: this.generateColumnDefs(), pinnedBottomRowData: [], isLoading: true }),
      catchError((error: unknown) => {
        console.error('Failed to load user analytics data:', error);
        return of({ data: [], columnDefs: this.generateColumnDefs(), pinnedBottomRowData: [], isLoading: false });
      }),
    );
  }

  fetchGtmData(): Observable<GTM[]> {
    const query = `
      SELECT 
        syllabus_id,
        syllabus_name,
        syllabus_short_name,
        syllabus_program,
        syllabus_subject,
        course_levels,
        exam_quarters
      FROM \`examdojo-analytics.00_dbt_05_business_intelligence.bi__gtm_analytics\`
      ORDER BY syllabus_program, syllabus_short_name
    `;

    return this.queryBigQuery(query);
  }

  fetchTableMetadata(): Observable<{ lastModified: Date }> {
    return this.bigQueryService.fetchTableMetadata('bi__gtm_analytics');
  }

  // Update totals row based on currently filtered/visible rows
  updateTotalsRowFromFilteredData(api: GridApi<UserAnalyticsData>): void {
    const filteredData: UserAnalyticsData[] = [];

    // Get all visible (filtered) leaf nodes
    api.forEachNodeAfterFilter((node: IRowNode<UserAnalyticsData>, _index: number) => {
      if (!node.group && node.data) {
        filteredData.push(node.data);
      }
    });

    // Calculate totals from filtered data
    const filteredTotalsRow = this.calculateTotalsRow(filteredData);

    // Update the pinned bottom row with filtered totals
    api.setGridOption('pinnedBottomRowData', filteredTotalsRow);
  }

  // Calculate totals row for pinned bottom
  private calculateTotalsRow(data: UserAnalyticsData[]): UserAnalyticsData[] {
    if (!data || data.length === 0) {
      return [];
    }

    // Calculate sums for numeric fields
    const totals = data.reduce(
      (acc, row) => {
        acc.subscribers += row.subscribers || 0;
        acc.total_size += row.total_size || 0;
        acc.activated += row.activated || 0;
        acc.wau += row.wau || 0;
        acc.mau += row.mau || 0;
        acc.ai_total_costs_eur += row.ai_total_costs_eur || 0;
        acc.ai_monthly_avg_eur += row.ai_monthly_avg_eur || 0;
        acc.grading_free_eur += row.grading_free_eur || 0;
        acc.chat_free_eur += row.chat_free_eur || 0;
        acc.grading_paid_eur += row.grading_paid_eur || 0;
        acc.chat_paid_eur += row.chat_paid_eur || 0;
        return acc;
      },
      {
        subscribers: 0,
        total_size: 0,
        activated: 0,
        wau: 0,
        mau: 0,
        ai_total_costs_eur: 0,
        ai_monthly_avg_eur: 0,
        grading_free_eur: 0,
        chat_free_eur: 0,
        grading_paid_eur: 0,
        chat_paid_eur: 0,
      },
    );

    // Calculate overall penetration rate
    const overallPenetrationRate = totals.total_size > 0 ? totals.subscribers / totals.total_size : 0;

    // Calculate overall activation rate - store as decimal like penetration_rate
    const overallActivationRate = totals.subscribers > 0 ? totals.activated / totals.subscribers : 0;

    const totalsRow: UserAnalyticsData = {
      syllabus_program: 'TOTAL', // This will show in the group column
      syllabus_short_name: '',
      exam_quarter: '',
      is_past_exam: false, // Not applicable for totals row
      subscribers: totals.subscribers,
      total_size: totals.total_size,
      penetration_rate: overallPenetrationRate,
      activated: totals.activated,
      activation_rate: overallActivationRate,
      wau: totals.wau,
      mau: totals.mau,
      ai_total_costs_eur: totals.ai_total_costs_eur,
      ai_monthly_avg_eur: totals.ai_monthly_avg_eur,
      grading_free_eur: totals.grading_free_eur,
      chat_free_eur: totals.chat_free_eur,
      grading_paid_eur: totals.grading_paid_eur,
      chat_paid_eur: totals.chat_paid_eur,
      // Don't include sparkline data in totals row
      subscribers_sparkline: undefined,
      wau_sparkline: undefined,
      mau_sparkline: undefined,
      grading_coin_usage_sparkline: undefined,
    };

    return [totalsRow];
  }

  // Transform API data to AG Grid format - flat structure with individual syllabus-quarter rows
  private transformApiDataToGridFormat(apiData: GTM[]): UserAnalyticsData[] {
    const flatRows: UserAnalyticsData[] = [];

    apiData.forEach((syllabus) => {
      // Sort exam quarters chronologically before processing
      const sortedQuarters = [...syllabus.exam_quarters].sort((a, b) => {
        return this.compareQuarters(a.exam_quarter, b.exam_quarter);
      });

      // Create individual rows for each exam quarter within this syllabus
      sortedQuarters.forEach((examQuarter) => {
        // Get current values from the time series data
        const subscribersValues = Object.values(examQuarter.weekly_cummulative_subscribers);
        const currentSubscribers = subscribersValues[subscribersValues.length - 1] || 0;

        const wauValues = Object.values(examQuarter.weekly_active_users);
        const currentWau = wauValues[wauValues.length - 1] || 0;

        const mauValues = Object.values(examQuarter.monthly_active_users);
        const currentMau = mauValues[mauValues.length - 1] || 0;

        // Calculate penetration rate
        const penetrationRate = examQuarter.total_size > 0 ? currentSubscribers / examQuarter.total_size : 0;

        // Calculate activation rate (activated users / subscribers) - store as decimal like penetration_rate
        const activationRate = currentSubscribers > 0 ? examQuarter.activated_users / currentSubscribers : 0;

        // Convert time series to sparkline data
        const subscribersSparkline = Object.entries(examQuarter.weekly_cummulative_subscribers).map(
          ([date, value]) => ({ date, value }),
        );
        const wauSparkline = Object.entries(examQuarter.weekly_active_users).map(([date, value]) => ({
          date,
          value,
        }));
        const mauSparkline = Object.entries(examQuarter.monthly_active_users).map(([date, value]) => ({
          date,
          value,
        }));
        const gradingCoinSparkline = Object.entries(examQuarter.ai_costs.grading_coin_median_usage).map(
          ([date, value]) => ({ date, value }),
        );

        const gridRow: UserAnalyticsData = {
          syllabus_program: syllabus.syllabus_program,
          syllabus_short_name: syllabus.syllabus_short_name,
          exam_quarter: examQuarter.exam_quarter,
          is_past_exam: examQuarter.is_past_exam,
          subscribers: currentSubscribers,
          total_size: examQuarter.total_size,
          penetration_rate: penetrationRate,
          activated: examQuarter.activated_users || 0,
          activation_rate: activationRate,
          wau: currentWau,
          mau: currentMau,
          subscribers_sparkline: subscribersSparkline.length > 0 ? subscribersSparkline : undefined,
          wau_sparkline: wauSparkline.length > 0 ? wauSparkline : undefined,
          mau_sparkline: mauSparkline.length > 0 ? mauSparkline : undefined,
          ai_total_costs_eur: examQuarter.ai_costs.total_to_date_in_eur,
          ai_monthly_avg_eur: examQuarter.ai_costs.monthly_avg_in_eur,
          grading_free_eur: examQuarter.ai_costs.free_subscribers.monthly_avg_grading_in_eur,
          chat_free_eur: examQuarter.ai_costs.free_subscribers.monthly_avg_chat_in_eur,
          grading_paid_eur: examQuarter.ai_costs.paid_subscribers.monthly_avg_grading_in_eur,
          chat_paid_eur: examQuarter.ai_costs.paid_subscribers.monthly_avg_chat_in_eur,
          grading_coin_usage_sparkline: gradingCoinSparkline.length > 0 ? gradingCoinSparkline : undefined,
        };

        flatRows.push(gridRow);
      });
    });

    return flatRows;
  }

  // Compare quarters chronologically (e.g., "Q2 25" < "Q4 25" < "Q2 26")
  private compareQuarters(quarterA: string, quarterB: string): number {
    const parseQuarter = (quarter: string): { year: number; q: number } => {
      const match = quarter.match(/Q(\d+)\s+(\d+)/);
      if (!match) {
        return { year: 0, q: 0 };
      }
      const q = parseInt(match[1], 10);
      const yearShort = parseInt(match[2], 10);
      // Convert 2-digit year to full year (25 -> 2025, 26 -> 2026, etc.)
      const year = yearShort < 50 ? 2000 + yearShort : 1900 + yearShort;
      return { year, q };
    };

    const parsedA = parseQuarter(quarterA);
    const parsedB = parseQuarter(quarterB);

    // First compare by year
    if (parsedA.year !== parsedB.year) {
      return parsedA.year - parsedB.year;
    }

    // Then compare by quarter within the same year
    return parsedA.q - parsedB.q;
  }

  private queryBigQuery(query: string): Observable<GTM[]> {
    console.log('Executing BigQuery:', query);

    return this.bigQueryService.queryAnalytics(query).pipe(
      map((response) => this.transformBigQueryResponse(response)),
      catchError((error: unknown) => {
        console.error('Error executing BigQuery:', error);
        return of([]);
      }),
    );
  }

  private transformBigQueryResponse(response: BigQueryResponse): GTM[] {
    if (!response.rows || !response.schema) {
      return [];
    }

    const result = response.rows.map((row: BigQueryRow) => {
      const rowData: Record<string, unknown> = {};

      response.schema.fields.forEach((field: BigQueryFieldSchema, index: number) => {
        const cellValue = row.f[index]?.v;
        rowData[field.name] = this.transformBigQueryField(cellValue, field);
      });

      // Transform to GTM interface
      const gtm = {
        syllabus_id: String(rowData['syllabus_id']),
        syllabus_name: rowData['syllabus_name'] as string,
        syllabus_short_name: rowData['syllabus_short_name'] as string,
        syllabus_program: rowData['syllabus_program'] as string,
        syllabus_subject: rowData['syllabus_subject'] as string,
        course_levels: rowData['course_levels'] ?? [],
        exam_quarters: rowData['exam_quarters'] ?? [],
      } as GTM;

      return gtm;
    });

    console.log('Transformed BigQuery data:', result);
    return result;
  }

  private transformBigQueryField(value: unknown, field: BigQueryFieldSchema): unknown {
    if (value === null || value === undefined) {
      return field.mode === 'REPEATED' ? [] : null;
    }

    // Handle REPEATED fields (arrays)
    if (field.mode === 'REPEATED') {
      if (!Array.isArray(value)) {
        return [];
      }

      // For RECORD type arrays (like exam_quarters)
      if (field.type === 'RECORD' && field.fields) {
        return value.map((item: BigQueryRepeatedValue) => {
          if (item?.v && typeof item.v === 'object' && item.v !== null && 'f' in item.v) {
            const rowValue = item.v as BigQueryRowValue;
            const recordData: Record<string, unknown> = {};
            field.fields!.forEach((subField: BigQueryFieldSchema, subIndex: number) => {
              const subValue = rowValue.f[subIndex]?.v;
              recordData[subField.name] = this.transformBigQueryField(subValue, subField);
            });

            // Add missing fields with default values for ExamQuarterEntry interface compatibility
            if (field.name === 'exam_quarters') {
              recordData['total_size'] = recordData['total_size'] ?? 0;
              recordData['activated_users'] = recordData['activated_users'] ?? 0;
              recordData['ai_costs'] = recordData['ai_costs'] ?? {
                total_to_date_in_eur: 0,
                monthly_avg_in_eur: 0,
                free_subscribers: {
                  monthly_avg_grading_in_eur: 0,
                  monthly_avg_chat_in_eur: 0,
                },
                paid_subscribers: {
                  monthly_avg_grading_in_eur: 0,
                  monthly_avg_chat_in_eur: 0,
                },
                grading_coin_median_usage: {},
              };
            }

            return recordData;
          }
          return (item as BigQueryRepeatedValue)?.v || item;
        });
      }

      // For simple REPEATED fields (like course_levels)
      return value.map((item: BigQueryRepeatedValue) => item?.v || item);
    }

    // Handle RECORD fields (nested objects)
    if (field.type === 'RECORD' && field.fields && value && typeof value === 'object' && 'f' in value) {
      const rowValue = value as BigQueryRowValue;
      const recordData: Record<string, unknown> = {};
      field.fields.forEach((subField: BigQueryFieldSchema, subIndex: number) => {
        const subValue = rowValue.f[subIndex]?.v;
        recordData[subField.name] = this.transformBigQueryField(subValue, subField);
      });
      return recordData;
    }

    // Handle JSON strings
    if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }

    // Convert numeric field types from strings to numbers
    if (field.type === 'INTEGER' || field.type === 'FLOAT' || field.type === 'NUMERIC') {
      if (typeof value === 'string') {
        const numValue = Number(value);
        return isNaN(numValue) ? 0 : numValue;
      }
      if (typeof value === 'number') {
        return value;
      }
      return 0;
    }

    return value;
  }

  private getProgressBarHTML(
    value: number,
    config: {
      thresholds?: { redBelow: number; yellowBelow: number };
      useRelativeColoring?: boolean;
      allValues?: number[];
    },
    showPercentage = true,
  ): string {
    const percentage = Math.round(value * 100);
    let barColor: string;
    let textColor: string;

    if (config.useRelativeColoring && config.allValues) {
      // Relative coloring: top 33% = green, middle 33% = yellow, bottom 33% = red
      const sortedValues = [...config.allValues].sort((a, b) => b - a);
      const topThreshold = sortedValues[Math.floor(sortedValues.length * 0.33)];
      const bottomThreshold = sortedValues[Math.floor(sortedValues.length * 0.67)];

      if (value >= topThreshold) {
        barColor = 'bg-green-500 dark:bg-green-400';
        textColor = 'text-green-600 dark:text-green-400';
      } else if (value >= bottomThreshold) {
        barColor = 'bg-yellow-500 dark:bg-yellow-400';
        textColor = 'text-yellow-600 dark:text-yellow-400';
      } else {
        barColor = 'bg-red-500 dark:bg-red-400';
        textColor = 'text-red-600 dark:text-red-400';
      }
    } else {
      // Static thresholds
      const thresholds = config.thresholds || { redBelow: 20, yellowBelow: 50 };

      if (percentage < thresholds.redBelow) {
        barColor = 'bg-red-500 dark:bg-red-400';
        textColor = 'text-red-600 dark:text-red-400';
      } else if (percentage < thresholds.yellowBelow) {
        barColor = 'bg-yellow-500 dark:bg-yellow-400';
        textColor = 'text-yellow-600 dark:text-yellow-400';
      } else {
        barColor = 'bg-green-500 dark:bg-green-400';
        textColor = 'text-green-600 dark:text-green-400';
      }
    }

    const displayText = showPercentage ? `${percentage}%` : percentage.toString();

    return `<div class="flex items-center gap-2 h-full">
      <div class="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
        <div class="${barColor} h-1.5 rounded-full transition-all duration-300" style="width: ${percentage}%"></div>
      </div>
      <span class="${textColor} font-semibold text-xs min-w-0">${displayText}</span>
    </div>`;
  }
}
